import useExperience from 'hooks/Experiences/useExperience'
import { useUserCartItemState } from './useUserCartItemState'
import { useContext, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { userCartItemsSaveForLater, userCartItemUpdate } from 'actions/UserCartActions'
import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'
import GeoContext from 'contexts/geoContext'
import { fetchTransferOptions } from 'actions/ExperienceActions'

type UserCartTransferItemData = {
  experience: App.ExperienceOffer
  loaded: true
  loading: false
  error: any
  itemTotals: App.Checkout.ItemViewTotals
  isDeleted: boolean
  isSavedForLater: boolean
  isBookable: boolean
  isSoldOut: boolean
  isPast: boolean
} | {
  experience: App.ExperienceOffer | undefined
  loaded: false
  loading: boolean
  error: any
  itemTotals: App.Checkout.ItemViewTotals
  isDeleted: boolean
  isSavedForLater: boolean
  isBookable: boolean
  isSoldOut: boolean
  isPast: boolean
}

function useUserCartTransferItemData(item: App.Checkout.TransferItem): UserCartTransferItemData {
  const dispatch = useAppDispatch()
  const [experience, experienceLoading, experienceError] = useExperience(item.experienceId)
  const { deleted, saveForLater } = useUserCartItemState(item.itemId)
  const { currentCurrency } = useContext(GeoContext)

  const transferOptionsState = useAppSelector(state => state.experience.experienceTransferOptions[item.experienceId])

  useEffect(() => {
    dispatch(fetchTransferOptions(item.experienceId))
  }, [dispatch, item.experienceId, currentCurrency])

  // Update the actual user cart item when we get fresh transfer pricing
  useEffect(() => {
    if (!transferOptionsState?.fetching && !transferOptionsState?.error && transferOptionsState?.transfers) {
      const freshTransferOption = transferOptionsState.transfers.find(
        (transfer: App.ExperienceTransferOption) => transfer.id === item.transfer.option?.id
      )
      const isPriceDifferent = item.transfer.option?.price !== freshTransferOption?.price

      if (freshTransferOption && item.transfer.option && isPriceDifferent) {
        const updatedItem: App.Checkout.TransferItem = {
          ...item,
          total: freshTransferOption.price,
          transfer: {
            ...item.transfer,
            option: {
              ...item.transfer.option,
              price: freshTransferOption.price,
              memberPrice: freshTransferOption.memberPrice,
              memberPriceCurrency: freshTransferOption.memberPriceCurrency,
              taxesAndFees: freshTransferOption.taxesAndFees,
              discounts: freshTransferOption.discounts,
            },
          },
        }

        dispatch(userCartItemUpdate(updatedItem))
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferOptionsState])

  const isSoldOut = !!experience && experience?.status !== 'ONLINE'
  const isPast = item.transfer.date ? dateIsBeforeOrSame(new Date(item.transfer.date), new Date()) : false
  const isBookable = experience?.status === 'ONLINE' && !experienceError && !isPast

  useEffect(() => {
    if (experience && !isBookable && !saveForLater && !experienceLoading && !deleted) {
      dispatch(userCartItemsSaveForLater([item.itemId]))
    }
  }, [isBookable, saveForLater, experienceLoading, dispatch, item.itemId, experience, deleted])

  const itemTotals: App.Checkout.ItemViewTotals = {
    price: item.transfer.option?.price ?? 0,
    memberPrice: item.transfer.option?.memberPrice ?? 0,
    value: item.transfer.option?.price ?? 0,
    mobileAppDiscount: {
      amount: item.transfer.option?.discounts?.app.amount ?? 0,
      percentage: item.transfer.option?.discounts?.app.percentage ?? 0,
    },
    taxesAndFees: item.transfer.option?.taxesAndFees ?? 0,
    surcharge: 0,
    memberValue: 0,
    extraGuestSurcharge: 0,
  }

  const loading = experienceLoading || !!transferOptionsState?.fetching
  const error = experienceError || transferOptionsState?.error

  if (experience) {
    return {
      experience,
      loaded: true,
      loading: false,
      error,
      itemTotals,
      isDeleted: deleted,
      isSavedForLater: saveForLater,
      isBookable,
      isSoldOut,
      isPast,
    }
  } else {
    return {
      experience: undefined,
      loaded: false,
      loading,
      error,
      itemTotals,
      isDeleted: deleted,
      isSavedForLater: saveForLater,
      isBookable,
      isSoldOut,
      isPast,
    }
  }
}

export default useUserCartTransferItemData
